import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ================= CUSTOM NODES =================

const INTERVAL_OPTS = [
  { v: '1m', l: '1 min' }, { v: '3m', l: '3 min' }, { v: '5m', l: '5 min' },
  { v: '3h', l: '3 hours' }, { v: '6h', l: '6 hours' }, { v: '12h', l: '12 hours' },
  { v: '18h', l: '18 hours' }, { v: '23h', l: '23 hours' },
];

const TriggerNode = ({ data }) => {
  const [intOpen, setIntOpen] = useState(false);
  const selectedIntervals = data.inactivityIntervals && data.inactivityIntervals.length ? data.inactivityIntervals : ['3h'];
  const toggleInterval = (v) => {
    const next = selectedIntervals.includes(v) ? selectedIntervals.filter(x => x !== v) : [...selectedIntervals, v];
    data.onTriggerConfig?.({ inactivityIntervals: next.length ? next : ['3h'] });
  };
  return (
    <div style={{ position: 'relative', background: '#FFFFFF', borderRadius: '14px', border: '1px solid #E7E1D6', width: '240px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
      <div style={{ background: '#FAF7F1', padding: '8px 12px', borderBottom: '1px solid #E7E1D6', display: 'flex', alignItems: 'center', gap: '6px', borderTopLeftRadius: '13px', borderTopRightRadius: '13px' }}>
        <span style={{ display: 'flex', color: '#111111' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg></span>
        <div style={{ fontWeight: 700, color: '#111111', fontSize: '12px', letterSpacing: '-0.03em' }}>{data.triggerType === 'event' ? 'TRIGGER By Event' : data.triggerType === 'inactivity' ? 'TRIGGER By Inactivity' : data.triggerType === 'chat_expiry_reminder' ? 'TRIGGER By Chat Expiry' : 'TRIGGER By Intent'}</div>
      </div>
      <div style={{ padding: '12px' }}>
        {data.triggerType === 'inactivity' ? (
          <div style={{ position: 'relative' }}>
            <p style={{ fontSize: '11px', color: '#6B6862', margin: '0 0 6px 0', fontWeight: 700 }}>No customer reply for:</p>
            <button type="button" className="nodrag" onClick={(e) => { e.stopPropagation(); setIntOpen(o => !o); }}
              style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', background: 'white', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedIntervals.map(v => (INTERVAL_OPTS.find(o => o.v === v) || {}).l || v).join(', ')}
              </span>
              <span style={{ color: '#9C978C', display: 'flex' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg></span>
            </button>
            {intOpen && (
              <div className="nodrag" onClick={(e) => e.stopPropagation()}
                style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#FFFFFF', border: '1px solid #E7E1D6', borderRadius: 8, boxShadow: '4px 4px 0 rgba(17,17,17,.2)', zIndex: 50, maxHeight: 190, overflowY: 'auto', padding: 4 }}>
                {INTERVAL_OPTS.map(o => (
                  <label key={o.v} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 12, cursor: 'pointer', borderRadius: 6, color: '#3A3A37' }}>
                    <input type="checkbox" checked={selectedIntervals.includes(o.v)} onChange={() => toggleInterval(o.v)} />
                    {o.l}
                  </label>
                ))}
              </div>
            )}
            <p style={{ fontSize: '10px', color: '#9C978C', margin: '6px 0 0' }}>Sends at each selected mark since the last reply.</p>
          </div>
        ) : data.triggerType === 'chat_expiry_reminder' ? (
          <div>
            <p style={{ fontSize: '11px', color: '#6B6862', margin: '0 0 6px 0', fontWeight: 700 }}>Trigger when time remaining:</p>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="number" className="nodrag" min="0" max="23" value={data.expiryH ?? '2'} onChange={(e) => data.onTriggerConfig?.({ expiryH: e.target.value })}
                placeholder="hh" style={{ width: '50%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', boxSizing: 'border-box' }} />
              <span style={{ fontWeight: 800, color: '#6B6862' }}>:</span>
              <input type="number" className="nodrag" min="0" max="59" value={data.expiryM ?? '0'} onChange={(e) => data.onTriggerConfig?.({ expiryM: e.target.value })}
                placeholder="mm" style={{ width: '50%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <p style={{ fontSize: '10px', color: '#9C978C', margin: '6px 0 0' }}>hours : minutes before the 24h window closes.</p>
          </div>
        ) : data.triggerType === 'event' ? (
          <>
            <p style={{ fontSize: '11px', color: '#6B6862', margin: '0 0 6px 0' }}>When this happens:</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 10px', fontSize: '12px', fontWeight: 700, color: '#111111', background: '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: '6px' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#111111', flex: 'none' }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {data.eventValue === 'new_order' ? 'New order' : data.eventValue === 'new_abandoned_checkout' ? 'New abandoned checkout' : data.eventValue === 'new_message' ? 'New message received' : (data.eventValue || '—')}
              </span>
            </div>
          </>
        ) : (
          <>
            <p style={{ fontSize: '11px', color: '#6B6862', margin: '0 0 6px 0' }}>When AI detects this intent:</p>
            {data.locked ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 10px', fontSize: '12px', fontWeight: 700, color: '#111111', background: '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: '6px' }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#111111', flex: 'none' }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{data.intentValue || '—'}</span>
              </div>
            ) : (
              <select
                className="nodrag"
                value={data.intentValue || ''}
                onChange={(e) => data.onChange(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none' }}>
                <option value="">Select an Intent...</option>
                {data.availableIntents?.map(tag => (
                  <option key={tag._id} value={tag.name}>{tag.name} - {tag.description?.substring(0, 30)}</option>
                ))}
              </select>
            )}
          </>
        )}
      </div>
      {data.triggerType === 'inactivity' ? (
        <div style={{ borderTop: '1px solid #E7E1D6', padding: '8px 12px 10px' }}>
          {[
            { id: 'reminder', label: 'On each interval', color: '#111111' },
            { id: 'timeout', label: 'All intervals passed', color: '#8A5A12' },
            { id: 'resolved', label: 'Agent pressed Resolve', color: '#0F6E56' },
          ].map(o => (
            <div key={o.id} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, padding: '5px 0', fontSize: 10.5, fontWeight: 700, color: '#3A3A37' }}>
              <span>{o.label}</span>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.color, flex: 'none' }} />
              <Handle type="source" position={Position.Right} id={o.id} style={{ width: 10, height: 10, background: o.color, border: '2px solid white', right: '-6px', top: '50%' }} />
            </div>
          ))}
        </div>
      ) : (
        <Handle type="source" position={Position.Right} style={{ width: '10px', height: '10px', background: '#111111', border: '2px solid white', right: '-6px' }} />
      )}
    </div>
  );
};

const ActionNode = ({ data, id }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div 
      onMouseEnter={() => setHovered(true)} 
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', background: '#FFFFFF', borderRadius: '14px', border: '1px solid #E7E1D6', width: '240px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
      
      {hovered && (
        <button 
          onClick={() => data.onDelete(id)}
          style={{ position: 'absolute', top: '-2px', right: '-2px', background: '#B23A3A', color: 'white', width: '24px', height: '24px', border: 'none', borderRadius: '0 12px 0 12px', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>
          &times;
        </button>
      )}

      <Handle type="target" position={Position.Left} style={{ width: '10px', height: '10px', background: '#111111', border: '2px solid white', left: '-6px' }} />
      <div style={{ background: '#FAF7F1', padding: '8px 12px', borderBottom: '1px solid #E7E1D6', display: 'flex', alignItems: 'center', gap: '6px', borderTopLeftRadius: '13px', borderTopRightRadius: '13px' }}>
        <span style={{ display: 'flex', color: '#111111' }}>{data.actionType === 'create_notification' ? (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>) : data.actionType === 'create_ticket' ? (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9a3 3 0 0 0 0 6v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" /></svg>) : (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>)}</span>
        <div style={{ fontWeight: 700, color: '#111111', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '-0.03em' }}>
          {data.actionType === 'send_whatsapp_message' && 'Send Message'}
          {data.actionType === 'send_whatsapp_template' && 'Send Template'}
          {data.actionType === 'send_website_message' && 'Website Message'}
          {data.actionType === 'send_instagram_message' && 'Instagram Message'}
          {data.actionType === 'send_facebook_message' && 'Messenger Message'}
          {data.actionType === 'create_notification' && 'Create Notification'}
          {data.actionType === 'create_ticket' && 'Generate Ticket'}
          {data.actionType === 'send_message_with_button' && 'Message + Button'}
          {data.actionType === 'ask_query_resolved' && 'Ask: Query Resolved?'}
          {data.actionType === 'kill_inactivity_trigger' && 'Kill Inactivity Trigger'}
          {data.actionType === 'restart_inactivity_trigger' && 'Restart Inactivity Trigger'}
        </div>
      </div>
      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>


        {(data.actionType === 'send_whatsapp_message' || data.actionType === 'send_website_message' || data.actionType === 'send_instagram_message' || data.actionType === 'send_facebook_message') && (
          <div>
            <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Message Body</label>
            <textarea
              className="nodrag"
              value={data.textBody || ''}
              onChange={(e) => data.onContentChange(id, { textBody: e.target.value })}
              placeholder="e.g. Please wait..."
              style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', minHeight: '60px', boxSizing: 'border-box' }}
            />
          </div>
        )}

        {data.actionType === 'send_whatsapp_template' && (() => {
          const selectedTpl = data.availableTemplates?.find(t => t.name === data.templateName);
          // Variables in header (TEXT) then body, matching the backend's order.
          const varList = [];
          (selectedTpl?.components || []).forEach(c => {
            if (c.type === 'HEADER' && c.format === 'TEXT') {
              (c.text?.match(/\{\{\d+\}\}/g) || []).forEach((v, i) => varList.push({ where: 'Header', token: v, ex: c.example?.header_text?.[i] }));
            } else if (c.type === 'BODY') {
              (c.text?.match(/\{\{\d+\}\}/g) || []).forEach((v, i) => varList.push({ where: 'Body', token: v, ex: (c.example?.body_text && c.example.body_text[0]?.[i]) }));
            }
          });
          const params = data.templateParams || [];
          const setParam = (idx, val) => {
            const next = [...params];
            next[idx] = val;
            data.onContentChange(id, { templateParams: next });
          };
          // Tokens used by auto-fill (in variable order) + the per-field chips.
          const autoTokens = data.eventValue === 'new_abandoned_checkout'
            ? ['{store}', '{items}', '{price}', '{customer}', '{email}', '{phone}', '{url}']
            : ['{store}', '{order_number}', '{items}', '{price}', '{customer}', '{email}', '{phone}'];
          const chipTokens = data.eventValue === 'new_abandoned_checkout'
            ? [['Store', '{store}'], ['Items', '{items}'], ['Price', '{price}'], ['Customer', '{customer}'], ['Link', '{url}']]
            : [['Store', '{store}'], ['Order #', '{order_number}'], ['Items', '{items}'], ['Price', '{price}'], ['Customer', '{customer}']];
          const toggleAutoFill = () => {
            if (data.autoFill) data.onContentChange(id, { autoFill: false, templateParams: varList.map(() => '') });
            else data.onContentChange(id, { autoFill: true, templateParams: varList.map((_, i) => autoTokens[i] || '') });
          };
          return (
            <div>
              <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Select Template</label>
              <select
                className="nodrag"
                value={data.templateName || ''}
                onChange={(e) => {
                  const tpl = data.availableTemplates?.find(t => t.name === e.target.value);
                  const btns = (tpl?.components?.find(c => c.type === 'BUTTONS')?.buttons || [])
                    .filter(b => b.type === 'QUICK_REPLY').map(b => b.text);
                  data.onContentChange(id, { templateName: e.target.value, templateLanguage: tpl?.language || '', templateParams: [], templateButtons: btns });
                }}
                style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none' }}>
                <option value="">Choose a template...</option>
                {data.availableTemplates?.map(t => (
                  <option key={t.id} value={t.name}>{t.name} ({t.language})</option>
                ))}
              </select>

              {selectedTpl && varList.length > 0 && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase' }}>Variable Values</label>
                    {data.triggerType === 'event' && (
                      <button type="button" className="nodrag" onClick={toggleAutoFill}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: 800, color: data.autoFill ? '#fff' : '#111111', background: data.autoFill ? '#111111' : '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: '20px', padding: '3px 9px', cursor: 'pointer' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: data.autoFill ? '#fff' : '#9C978C' }} />
                        Auto-fill
                      </button>
                    )}
                  </div>
                  {varList.map((v, i) => (
                    <div key={i}>
                      <input
                        className="nodrag"
                        value={params[i] ?? ''}
                        onChange={(e) => setParam(i, e.target.value)}
                        placeholder={`${v.where} ${v.token}${v.ex ? ` — e.g. ${v.ex}` : ''}`}
                        style={{ width: '100%', padding: '7px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', boxSizing: 'border-box' }}
                      />
                      {data.triggerType === 'event' && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {chipTokens.map(([label, tok]) => (
                            <button key={tok} type="button" className="nodrag" onClick={() => setParam(i, tok)}
                              style={{ fontSize: '9.5px', fontWeight: 700, color: '#111111', background: '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: '5px', padding: '2px 6px', cursor: 'pointer' }}>
                              + {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <p style={{ fontSize: '10px', color: '#9C978C', margin: 0, lineHeight: 1.4 }}>
                    {data.triggerType === 'event' ? 'Auto-fill maps every field to live order data. Tokens like {price}, {store}, {items} also work in any other block. ' : ''}Leave blank to use the template's sample. {'{phone_number}'} works too.
                  </p>
                </div>
              )}

              {(data.templateButtons || []).length > 0 && (
                <div style={{ marginTop: '12px', borderTop: '1px dashed #E7E1D6', paddingTop: '10px' }}>
                  <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Buttons → branches</label>
                  {data.templateButtons.map((b, i) => (
                    <div key={i} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#111111', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#111111' }} />{b}
                      </span>
                      <Handle type="source" position={Position.Right} id={`btn-${i}`} style={{ right: '-22px', top: '50%', width: '10px', height: '10px', background: '#111111', border: '2px solid white' }} />
                    </div>
                  ))}
                  <p style={{ fontSize: '10px', color: '#9C978C', margin: '4px 0 0', lineHeight: 1.4 }}>Wire each button to a block — the workflow waits for the customer's tap, then follows that branch.</p>
                </div>
              )}
            </div>
          );
        })()}

        {data.actionType === 'create_notification' && (
          <>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Category (Tag)</label>
              <input 
                type="text"
                className="nodrag"
                value={data.notificationCategory || ''}
                onChange={(e) => data.onContentChange(id, { notificationCategory: e.target.value })}
                placeholder="e.g. Refund Request"
                style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Notification Message</label>
              <textarea 
                className="nodrag"
                value={data.notificationContext || ''}
                onChange={(e) => data.onContentChange(id, { notificationContext: e.target.value })}
                placeholder="Use {phone_number} to insert the customer's number..."
                style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', minHeight: '60px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Critical Level</label>
              <select 
                className="nodrag"
                value={data.notificationLevel || 'medium'}
                onChange={(e) => data.onContentChange(id, { notificationLevel: e.target.value })}
                style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none' }}>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </>
        )}
        {data.actionType === 'create_ticket' && (
          <>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Ticket Category</label>
              <input 
                type="text"
                className="nodrag"
                value={data.ticketCategory || ''}
                onChange={(e) => data.onContentChange(id, { ticketCategory: e.target.value })}
                placeholder="e.g. Sales, Support, Complaint"
                style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Assigned To</label>
              <select 
                className="nodrag"
                value={data.ticketAssignedTo || ''}
                onChange={(e) => data.onContentChange(id, { ticketAssignedTo: e.target.value })}
                style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', boxSizing: 'border-box', background: 'white' }}
              >
                <option value="">Choose a user...</option>
                {data.workspaceUsers?.map(u => (
                  <option key={u._id} value={u.name || u.email}>{u.name || u.email}</option>
                ))}
                <option value="Unassigned">Unassigned</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '10px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>Query/Context Note</label>
              <textarea 
                className="nodrag"
                value={data.ticketQuery || ''}
                onChange={(e) => data.onContentChange(id, { ticketQuery: e.target.value })}
                placeholder="Details... (Supports {phone_number})"
                style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #9C978C', outline: 'none', minHeight: '60px', boxSizing: 'border-box' }}
              />
            </div>
          </>
        )}

        {data.actionType === 'send_message_with_button' && (() => {
          const btns = (data.buttons && data.buttons.length) ? data.buttons : [data.buttonText || 'Yes, please'];
          const setBtns = (next) => data.onContentChange(id, { buttons: next, buttonText: next[0] || '' });
          return (
            <>
              <label className="nodrag" style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Message</label>
              <textarea className="nodrag" value={data.textBody || ''} onChange={(e) => data.onContentChange(id, { textBody: e.target.value })}
                placeholder="Our Customer Support Team is busy…"
                style={{ width: '100%', minHeight: 62, padding: '9px 10px', fontSize: 12, borderRadius: 8, border: '1px solid #E7E1D6', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', background: '#FAF7F1', marginBottom: 11 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <label className="nodrag" style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', margin: 0 }}>Buttons</label>
                {btns.length < 3 && (
                  <button className="nodrag" onClick={() => setBtns([...btns, `Option ${btns.length + 1}`])}
                    style={{ background: '#FAF7F1', color: '#111111', border: 'none', borderRadius: 6, fontSize: 10.5, fontWeight: 800, padding: '3px 9px', cursor: 'pointer' }}>+ Add button</button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {btns.map((b, i) => (
                  <div key={i} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                      <input className="nodrag" value={b} maxLength={20}
                        onChange={(e) => { const n = [...btns]; n[i] = e.target.value; setBtns(n); }}
                        placeholder={`Button ${i + 1}`}
                        style={{ width: '100%', padding: '8px 44px 8px 10px', fontSize: 12, borderRadius: 8, border: `1px solid ${(b || '').length >= 20 ? '#8A5A12' : '#E7E1D6'}`, outline: 'none', boxSizing: 'border-box', background: '#fff', color: '#111111' }} />
                      <span className="nodrag" style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 9.5, fontWeight: 800, color: (b || '').length >= 20 ? '#8A5A12' : '#9C978C' }}>{(b || '').length}/20</span>
                    </div>
                    {btns.length > 1 && (
                      <button className="nodrag" onClick={() => setBtns(btns.filter((_, j) => j !== i))}
                        style={{ background: 'transparent', border: 'none', color: '#9C978C', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', flex: 'none' }}>×</button>
                    )}
                    <Handle type="source" position={Position.Right} id={`btn-${i}`}
                      style={{ position: 'absolute', right: -22, top: '50%', width: 10, height: 10, background: '#111111', border: '2px solid white' }} />
                  </div>
                ))}
              </div>
              <p className="nodrag" style={{ fontSize: 9.5, color: '#9C978C', margin: '8px 0 0', lineHeight: 1.4 }}>Each button can start its own branch →. WhatsApp caps button labels at 20 chars — put longer text in the message above. Leave a button unwired to just remind.</p>
            </>
          );
        })()}
        {data.actionType === 'ask_query_resolved' && (
          <>
            <label className="nodrag" style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>Question</label>
            <textarea className="nodrag" value={data.question || ''} onChange={(e) => data.onContentChange(id, { question: e.target.value })}
              placeholder="Please tell us if your query was resolved"
              style={{ width: '100%', minHeight: 54, padding: '9px 10px', fontSize: 12, borderRadius: 8, border: '1px solid #E7E1D6', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', background: '#FAF7F1', marginBottom: 11 }} />
            <label className="nodrag" style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 7 }}>Answer branches</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0F6E56', flex: 'none' }} />
                <input className="nodrag" value={data.yesLabel || 'Yes'} maxLength={20} onChange={(e) => data.onContentChange(id, { yesLabel: e.target.value })}
                  style={{ flex: 1, minWidth: 0, padding: '8px 10px', fontSize: 12, fontWeight: 700, borderRadius: 8, border: '1px solid #0F6E56', outline: 'none', boxSizing: 'border-box', background: '#E4F1E9', color: '#0F6E56' }} />
                <Handle type="source" position={Position.Right} id="yes" style={{ position: 'absolute', right: -22, top: '50%', width: 10, height: 10, background: '#0F6E56', border: '2px solid white' }} />
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#B23A3A', flex: 'none' }} />
                <input className="nodrag" value={data.noLabel || 'No'} maxLength={20} onChange={(e) => data.onContentChange(id, { noLabel: e.target.value })}
                  style={{ flex: 1, minWidth: 0, padding: '8px 10px', fontSize: 12, fontWeight: 700, borderRadius: 8, border: '1px solid #B23A3A', outline: 'none', boxSizing: 'border-box', background: '#F6E4E1', color: '#B23A3A' }} />
                <Handle type="source" position={Position.Right} id="no" style={{ position: 'absolute', right: -22, top: '50%', width: 10, height: 10, background: '#B23A3A', border: '2px solid white' }} />
              </div>
            </div>
          </>
        )}
        {data.actionType === 'kill_inactivity_trigger' && (
          <div className="nodrag" style={{ fontSize: 11, color: '#6B6862' }}>Stops the inactivity loop and marks the chat Resolved. Wire this from the Yes branch.</div>
        )}
        {data.actionType === 'restart_inactivity_trigger' && (
          <div className="nodrag" style={{ fontSize: 11, color: '#6B6862' }}>Restarts the inactivity intervals from now (clears fired marks). Wire this e.g. from the No branch.</div>
        )}

      </div>
      {/* Templates with quick-reply buttons expose one handle per button instead. */}
      {(data.actionType === 'ask_query_resolved' || data.actionType === 'send_message_with_button' || data.actionType === 'kill_inactivity_trigger' || data.actionType === 'restart_inactivity_trigger') ? null : (
        !(data.actionType === 'send_whatsapp_template' && (data.templateButtons || []).length > 0) && (
          <Handle type="source" position={Position.Right} style={{ width: '10px', height: '10px', background: '#111111', border: '2px solid white', right: '-6px' }} />
        )
      )}
    </div>
  );
};

// Confirm/Cancel Order — tag the Shopify order via write_orders. WhatsApp-only.
const ShopifyTagNode = ({ data, id, kind }) => {
  const [hovered, setHovered] = useState(false);
  const confirm = kind === 'confirm';
  const color = confirm ? '#0F6E56' : '#B23A3A';
  const bg = confirm ? '#E4F1E9' : '#F6E4E1';
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', background: '#FFFFFF', borderRadius: 14, border: '1px solid #E7E1D6', width: 230, boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
      {hovered && (
        <button onClick={() => data.onDelete(id)}
          style={{ position: 'absolute', top: -10, right: -10, background: '#B23A3A', color: 'white', width: 24, height: 24, border: 'none', borderRadius: '50%', cursor: 'pointer', zIndex: 10, fontSize: 14 }}>×</button>
      )}
      <Handle type="target" position={Position.Left} style={{ width: 10, height: 10, background: color, border: '2px solid white', left: -6 }} />
      <div style={{ background: bg, padding: '8px 12px', borderBottom: '1px solid #E7E1D6', display: 'flex', alignItems: 'center', gap: 6, borderTopLeftRadius: 13, borderTopRightRadius: 13 }}>
        <span style={{ display: 'flex', color }}>{confirm ? (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>) : (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>)}</span>
        <div style={{ fontWeight: 700, color, fontSize: 12, textTransform: 'uppercase', letterSpacing: '-0.03em' }}>{confirm ? 'Confirm Order' : 'Cancel Order'}</div>
      </div>
      <div style={{ padding: 12, fontSize: 11, color: '#6B6862', lineHeight: 1.5 }}>
        Tags the Shopify order <strong style={{ color }}>{confirm ? 'Confirmed VB' : 'Cancelled VB'}</strong> via the Orders API.
      </div>
      <Handle type="source" position={Position.Right} style={{ width: 10, height: 10, background: color, border: '2px solid white', right: -6 }} />
    </div>
  );
};
const ConfirmOrderNode = (p) => <ShopifyTagNode {...p} kind="confirm" />;
const CancelOrderNode = (p) => <ShopifyTagNode {...p} kind="cancel" />;

const ConditionNode = ({ data, id }) => {
  const [hovered, setHovered] = useState(false);
  const options = data.options || ['Option 1', 'Option 2'];

  const onOptionChange = (idx, val) => {
    const newOptions = [...options];
    newOptions[idx] = val;
    data.onContentChange(id, { options: newOptions });
  };

  const addOption = () => {
    data.onContentChange(id, { options: [...options, `Option ${options.length + 1}`] });
  };

  const removeOption = (idx) => {
    if (options.length <= 1) return;
    const newOptions = options.filter((_, i) => i !== idx);
    data.onContentChange(id, { options: newOptions });
  };

  return (
    <div 
      onMouseEnter={() => setHovered(true)} 
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', background: '#FFFFFF', borderRadius: '14px', border: '1px solid #E7E1D6', width: '260px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
      
      {hovered && (
        <button 
          onClick={() => data.onDelete(id)}
          style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#B23A3A', color: 'white', width: '24px', height: '24px', border: 'none', borderRadius: '50%', cursor: 'pointer', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold' }}>
          &times;
        </button>
      )}

      <Handle type="target" position={Position.Left} style={{ width: '10px', height: '10px', background: '#111111', border: '2px solid white', left: '-6px' }} />
      
      <div style={{ background: '#FAF7F1', padding: '10px 14px', borderBottom: '1px solid #E7E1D6', display: 'flex', alignItems: 'center', gap: '8px', borderTopLeftRadius: '13px', borderTopRightRadius: '13px' }}>
        <span style={{ display: 'flex', color: '#111111' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2a3 3 0 0 0-3 3 3.5 3.5 0 0 0-1.5 6.5A3 3 0 0 0 6 17a3 3 0 0 0 3.5 3V2z" /><path d="M14.5 2a3 3 0 0 1 3 3 3.5 3.5 0 0 1 1.5 6.5A3 3 0 0 1 18 17a3 3 0 0 1-3.5 3V2z" /></svg></span>
        <div style={{ fontWeight: 700, color: '#111111', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Condition Checker</div>
      </div>

      <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <label style={{ fontSize: '10px', fontWeight: '800', color: '#6B6862', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>Decision Statement</label>
          <textarea 
            className="nodrag"
            value={data.statement || ''}
            onChange={(e) => data.onContentChange(id, { statement: e.target.value })}
            placeholder="e.g. Determine if the user wants to cancel or modify their order."
            style={{ width: '100%', padding: '10px', fontSize: '12px', borderRadius: '8px', border: '1px solid #E7E1D6', outline: 'none', minHeight: '80px', boxSizing: 'border-box', background: '#FAF7F1' }}
          />
        </div>

        <div>
           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
             <label style={{ fontSize: '10px', fontWeight: '800', color: '#6B6862', textTransform: 'uppercase', margin: 0 }}>Decision Branches</label>
             <button 
              onClick={addOption}
              style={{ background: '#111111', color: 'white', border: 'none', borderRadius: '4px', fontSize: '10px', padding: '2px 6px', cursor: 'pointer', fontWeight: '700' }}
             >+ Add Branch</button>
           </div>
           
           <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
             {options.map((opt, idx) => (
               <div key={idx} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                 <input 
                   type="text"
                   className="nodrag"
                   value={opt}
                   onChange={(e) => onOptionChange(idx, e.target.value)}
                   style={{ width: '90%', padding: '8px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #E7E1D6', outline: 'none', background: '#fff' }}
                 />
                 {options.length > 1 && (
                   <button 
                    onClick={() => removeOption(idx)}
                    style={{ position: 'absolute', right: '35px', background: 'transparent', color: '#9C978C', border: 'none', cursor: 'pointer', fontSize: '14px' }}
                   >&times;</button>
                 )}
                 <Handle 
                  type="source" 
                  position={Position.Right} 
                  id={`handle-${idx}`}
                  style={{ top: '50%', right: '-32px', width: '10px', height: '10px', background: '#111111', border: '2px solid white' }} 
                 />
               </div>
             ))}
           </div>
        </div>
      </div>
    </div>
  );
};

const AiAgentNode = ({ data, id }) => {
  const [hovered, setHovered] = useState(false);
  const selected = data.availableAgents?.find(a => a.uuid === data.aiAgentUuid);
  const hasStatus = !!selected && (selected.fields || []).includes('status');

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', background: '#FFFFFF', borderRadius: 14, border: '1px solid #E7E1D6', width: 260, boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
      {hovered && (
        <button onClick={() => data.onDelete(id)}
          style={{ position: 'absolute', top: -10, right: -10, background: '#B23A3A', color: 'white', width: 24, height: 24, border: 'none', borderRadius: '50%', cursor: 'pointer', zIndex: 10, fontSize: 14 }}>×</button>
      )}
      <Handle type="target" position={Position.Left} style={{ width: 10, height: 10, background: '#111111', border: '2px solid white', left: -6 }} />
      <div style={{ background: '#FAF7F1', padding: '10px 14px', borderBottom: '1px solid #E7E1D6', borderTopLeftRadius: 13, borderTopRightRadius: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'flex', color: '#111111' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="8" width="18" height="12" rx="2" /><path d="M12 2v4M8 14h.01M16 14h.01" /></svg></span>
        <div style={{ fontWeight: 700, color: '#111111', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Agent</div>
      </div>
      <div style={{ padding: 14 }}>
        <label style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Select Agent</label>
        <select className="nodrag" value={data.aiAgentUuid || ''}
          onChange={e => data.onContentChange(id, { aiAgentUuid: e.target.value, fieldsPreview: data.availableAgents?.find(a => a.uuid === e.target.value)?.fields || [] })}
          style={{ width: '100%', padding: 8, fontSize: 12, borderRadius: 6, border: '1px solid #9C978C', outline: 'none', marginBottom: 10 }}>
          <option value="">Choose an agent…</option>
          {(data.availableAgents || []).map(a => <option key={a.uuid} value={a.uuid}>{a.name}</option>)}
        </select>
        {selected && (
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Outputs (use as variables below)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(selected.fields || []).map(f => (
                <span key={f} style={{ background: '#FAF7F1', color: '#111111', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{`{${f}}`}</span>
              ))}
            </div>
          </div>
        )}

        <div style={{ borderTop: '1px dashed #E7E1D6', paddingTop: 10 }}>
          <label className="nodrag" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#111111' }}>
            <input type="checkbox" checked={!!data.preConfirmed}
              onChange={e => data.onContentChange(id, { preConfirmed: e.target.checked })}
              style={{ width: 14, height: 14, cursor: 'pointer' }} />
            Pre-confirmed (skip intro question)
          </label>
          {data.preConfirmed && (
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>What did the user already confirm?</label>
              <textarea className="nodrag" value={data.confirmedText || ''}
                onChange={e => data.onContentChange(id, { confirmedText: e.target.value })}
                placeholder="e.g. i want to process a refund for my placed order"
                style={{ width: '100%', padding: 8, fontSize: 12, borderRadius: 6, border: '1px solid #9C978C', outline: 'none', minHeight: 50, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
              <p style={{ fontSize: 10, color: '#6B6862', margin: '4px 0 0', lineHeight: 1.4 }}>
                This is seeded as the user's first message so the agent skips the redundant "do you want…?" question.
              </p>
            </div>
          )}
        </div>

        {hasStatus && (
          <div style={{ borderTop: '1px dashed #E7E1D6', marginTop: 12, paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#0F6E56', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0F6E56', display: 'inline-block' }}></span>
                YES (status = 1)
              </span>
              <Handle type="source" position={Position.Right} id="yes"
                style={{ width: 10, height: 10, background: '#0F6E56', border: '2px solid white', right: -22, top: '50%' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#B23A3A', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#B23A3A', display: 'inline-block' }}></span>
                NO (status = 0)
              </span>
              <Handle type="source" position={Position.Right} id="no"
                style={{ width: 10, height: 10, background: '#B23A3A', border: '2px solid white', right: -22, top: '50%' }} />
            </div>
          </div>
        )}
      </div>
      {!hasStatus && (
        <Handle type="source" position={Position.Right} style={{ width: 10, height: 10, background: '#111111', border: '2px solid white', right: -6 }} />
      )}
    </div>
  );
};

const ConfirmationAgentNode = ({ data, id }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', background: '#FFFFFF', borderRadius: 14, border: '1px solid #E7E1D6', width: 260, boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
      {hovered && (
        <button onClick={() => data.onDelete(id)}
          style={{ position: 'absolute', top: -10, right: -10, background: '#B23A3A', color: 'white', width: 24, height: 24, border: 'none', borderRadius: '50%', cursor: 'pointer', zIndex: 10, fontSize: 14 }}>×</button>
      )}
      <Handle type="target" position={Position.Left} style={{ width: 10, height: 10, background: '#111111', border: '2px solid white', left: -6 }} />
      <div style={{ background: '#FAF7F1', padding: '10px 14px', borderBottom: '1px solid #E7E1D6', borderTopLeftRadius: 13, borderTopRightRadius: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'flex', color: '#111111' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg></span>
        <div style={{ fontWeight: 700, color: '#111111', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Confirmation Agent</div>
      </div>
      <div style={{ padding: 14, paddingRight: 28 }}>
        <label style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>What to confirm?</label>
        <textarea className="nodrag" value={data.confirmSubject || ''}
          onChange={e => data.onContentChange(id, { confirmSubject: e.target.value })}
          placeholder="e.g. a refund / to place a wholesale order / delivery info"
          style={{ width: '100%', padding: 8, fontSize: 12, borderRadius: 6, border: '1px solid #9C978C', outline: 'none', minHeight: 60, boxSizing: 'border-box', marginBottom: 10, fontFamily: 'inherit', resize: 'vertical' }} />
        <p style={{ fontSize: 10, color: '#6B6862', margin: '0 0 10px 0', lineHeight: 1.4 }}>
          The agent will ask the user "Hey! Do you want <i>{data.confirmSubject || '…'}</i>?" and keep asking until a clear yes/no.
        </p>
        <label style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Outputs</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          <span style={{ background: '#FAF7F1', color: '#111111', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{`{status}`}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#0F6E56', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0F6E56', display: 'inline-block' }}></span>
              YES (status = 1)
            </span>
            <Handle type="source" position={Position.Right} id="yes"
              style={{ width: 10, height: 10, background: '#0F6E56', border: '2px solid white', right: -22, top: '50%' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#B23A3A', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#B23A3A', display: 'inline-block' }}></span>
              NO (status = 0)
            </span>
            <Handle type="source" position={Position.Right} id="no"
              style={{ width: 10, height: 10, background: '#B23A3A', border: '2px solid white', right: -22, top: '50%' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Shopify Orders block — reads stored orders, filters them, branches by fulfillment.
const ShopifyOrdersNode = ({ data, id }) => {
  const [hovered, setHovered] = useState(false);
  const filterByPhone = data.filterByPhone !== false;
  const excludeDelivered = data.excludeDelivered !== false;
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', background: '#FFFFFF', borderRadius: 14, border: '1px solid #E7E1D6', width: 272, boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
      {hovered && <button onClick={() => data.onDelete(id)} style={{ position: 'absolute', top: -10, right: -10, background: '#B23A3A', color: 'white', width: 24, height: 24, border: 'none', borderRadius: '50%', cursor: 'pointer', zIndex: 10, fontSize: 14 }}>×</button>}
      <Handle type="target" position={Position.Left} style={{ width: 10, height: 10, background: '#0F6E56', border: '2px solid white', left: -6 }} />
      <div style={{ background: '#FAF7F1', padding: '10px 14px', borderBottom: '1px solid #E7E1D6', borderTopLeftRadius: 13, borderTopRightRadius: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'flex', color: '#111111' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39A2 2 0 0 0 9.64 16h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg></span>
        <div style={{ fontWeight: 700, color: '#111111', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shopify Orders</div>
      </div>
      <div style={{ padding: 14, paddingRight: 28 }}>
        <p style={{ fontSize: 10.5, color: '#6B6862', margin: '0 0 10px', lineHeight: 1.4 }}>Reads the stored orders, applies the filters below, then branches on fulfillment status.</p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 700, color: '#3A3A37', marginBottom: 8, cursor: 'pointer' }}>
          <input type="checkbox" className="nodrag" checked={filterByPhone} onChange={e => data.onContentChange(id, { filterByPhone: e.target.checked })} />
          Filter by contact&apos;s phone
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 700, color: '#3A3A37', marginBottom: 10, cursor: 'pointer' }}>
          <input type="checkbox" className="nodrag" checked={excludeDelivered} onChange={e => data.onContentChange(id, { excludeDelivered: e.target.checked })} />
          Delivery status ≠ Delivered
        </label>
        <label style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Outputs</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
          <span style={{ background: '#E4F1E9', color: '#0F6E56', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{`{order_count}`}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#0F6E56', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0F6E56', display: 'inline-block' }} /> Fulfilled
            </span>
            <Handle type="source" position={Position.Right} id="fulfilled" style={{ width: 10, height: 10, background: '#0F6E56', border: '2px solid white', right: -22, top: '50%' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#8A5A12', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#8A5A12', display: 'inline-block' }} /> Unfulfilled
            </span>
            <Handle type="source" position={Position.Right} id="unfulfilled" style={{ width: 10, height: 10, background: '#8A5A12', border: '2px solid white', right: -22, top: '50%' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// PostEx block — live-tracks the incoming orders' tracking numbers; outputs {delivery_updates}.
const PostexNode = ({ data, id }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', background: '#FFFFFF', borderRadius: 14, border: '1px solid #E7E1D6', width: 252, boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
      {hovered && <button onClick={() => data.onDelete(id)} style={{ position: 'absolute', top: -10, right: -10, background: '#B23A3A', color: 'white', width: 24, height: 24, border: 'none', borderRadius: '50%', cursor: 'pointer', zIndex: 10, fontSize: 14 }}>×</button>}
      <Handle type="target" position={Position.Left} style={{ width: 10, height: 10, background: '#111111', border: '2px solid white', left: -6 }} />
      <div style={{ background: '#FAF7F1', padding: '10px 14px', borderBottom: '1px solid #E7E1D6', borderTopLeftRadius: 13, borderTopRightRadius: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'flex', color: '#111111' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg></span>
        <div style={{ fontWeight: 700, color: '#111111', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>PostEx Tracking</div>
      </div>
      <div style={{ padding: 14, paddingRight: 28 }}>
        <p style={{ fontSize: 10.5, color: '#6B6862', margin: '0 0 10px', lineHeight: 1.4 }}>Live‑tracks the orders from the connected branch via PostEx and builds a status summary.</p>
        <label style={{ fontSize: 10, fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Outputs</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <span style={{ background: '#FAF7F1', color: '#111111', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{`{delivery_updates}`}</span>
        </div>
      </div>
      <Handle type="source" position={Position.Right} style={{ width: 10, height: 10, background: '#111111', border: '2px solid white', right: -6 }} />
    </div>
  );
};

// ---- Block palette: inline stroke icons + a professional tile ----
const Svg = ({ children, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const ICONS = {
  message: <Svg><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Svg>,
  template: <Svg><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></Svg>,
  button: <Svg><rect x="3" y="8" width="18" height="8" rx="2" /><path d="M7 12h.01M11 12h6" /></Svg>,
  ask: <Svg><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 1 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></Svg>,
  bell: <Svg><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></Svg>,
  ticket: <Svg><path d="M3 9a3 3 0 0 0 0 6v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" /></Svg>,
  brain: <Svg><path d="M9.5 2a3 3 0 0 0-3 3 3.5 3.5 0 0 0-1.5 6.5A3 3 0 0 0 6 17a3 3 0 0 0 3.5 3V2z" /><path d="M14.5 2a3 3 0 0 1 3 3 3.5 3.5 0 0 1 1.5 6.5A3 3 0 0 1 18 17a3 3 0 0 1-3.5 3V2z" /></Svg>,
  robot: <Svg><rect x="3" y="8" width="18" height="12" rx="2" /><path d="M12 2v4M8 14h.01M16 14h.01" /></Svg>,
  check: <Svg><path d="M20 6 9 17l-5-5" /></Svg>,
  stop: <Svg><rect x="4" y="4" width="16" height="16" rx="2" /></Svg>,
  restart: <Svg><path d="M3 2v6h6" /><path d="M3.51 8a9 9 0 1 0 2.13-3.36L3 8" /></Svg>,
  cart: <Svg><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39A2 2 0 0 0 9.64 16h9.72a2 2 0 0 0 2-1.61L23 6H6" /></Svg>,
  truck: <Svg><rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></Svg>,
};
const BlockTile = ({ onClick, color, icon, title, sub }) => (
  <button onClick={onClick}
    onMouseEnter={e => { e.currentTarget.style.borderColor = '#111111'; e.currentTarget.style.background = '#FAF7F1'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = '#E7E1D6'; e.currentTarget.style.background = '#FFFFFF'; }}
    style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', background: '#FFFFFF', border: '1px solid #E7E1D6', borderRadius: 14, padding: '11px 13px', cursor: 'pointer', transition: 'all .12s' }}>
    <span style={{ width: 34, height: 34, borderRadius: 9, background: '#FAF7F1', color: '#111111', display: 'grid', placeItems: 'center', flex: 'none' }}>{icon}</span>
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#111111' }}>{title}</span>
      {sub && <span style={{ display: 'block', fontSize: 11, color: '#9C978C', fontWeight: 600 }}>{sub}</span>}
    </span>
  </button>
);
const PaletteSection = ({ label, children }) => (
  <div style={{ marginBottom: 4 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, color: '#9C978C', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '2px 0 8px' }}>{label}</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>{children}</div>
  </div>
);

// ================= MAIN COMPONENT =================

const AutomationEditor = () => {
  const { workspaceUuid, automationId } = useParams();
  const navigate = useNavigate();
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [workflowName, setWorkflowName] = useState('New Workflow');
  const [platform, setPlatform] = useState('whatsapp');       // primary platform (platforms[0]) — drives the block palette
  const [platforms, setPlatforms] = useState(['whatsapp']);    // all target platforms
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState('paused'); // 'paused' | 'testing' | 'active'
  // Read-only (test/demo) account: can open + preview flows but cannot save or
  // change status. The editor route is outside PermissionsProvider, so we read
  // the flag persisted by PermissionsContext. Backend blocks the writes anyway.
  const readOnly = (typeof localStorage !== 'undefined' && localStorage.getItem('readOnly') === '1');
  const [testContacts, setTestContacts] = useState({ whatsapp: '', instagram: '', facebook: '', website: '' });
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [aiAgents, setAiAgents] = useState([]);
  // Intent chosen during the "New Automation" step — locked here (read-only in the trigger block)
  const [lockedIntent, setLockedIntent] = useState(null);
  // The workflow's trigger ({ type:'intent'|'event', value }) decided at creation
  const [triggerInfo, setTriggerInfo] = useState({ type: 'intent', value: '' });

  const [showAddBlockPopup, setShowAddBlockPopup] = useState(false);

  // Data bindings for the nodes
  const nodeTypes = useMemo(() => ({
    triggerNode: TriggerNode,
    actionNode: ActionNode,
    conditionNode: ConditionNode,
    aiAgentNode: AiAgentNode,
    confirmationAgentNode: ConfirmationAgentNode,
    confirmOrderNode: ConfirmOrderNode,
    cancelOrderNode: CancelOrderNode,
    shopifyOrdersNode: ShopifyOrdersNode,
    postexNode: PostexNode
  }), []);

  // Handlers for node updates
  const updateTriggerIntent = useCallback((newIntent) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === 'trigger-1') {
          return { ...n, data: { ...n.data, intentValue: newIntent } };
        }
        return n;
      })
    );
  }, [setNodes]);

  const onTriggerConfig = useCallback((payload) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === 'trigger-1') {
          return { ...n, data: { ...n.data, ...payload } };
        }
        return n;
      })
    );
  }, [setNodes]);

  const updateActionType = useCallback((nodeId, type) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === nodeId) {
          return { ...n, data: { ...n.data, actionType: type } };
        }
        return n;
      })
    );
  }, [setNodes]);

  const updateActionContent = useCallback((nodeId, payload) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === nodeId) {
          return { ...n, data: { ...n.data, ...payload } };
        }
        return n;
      })
    );
  }, [setNodes]);

  const deleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  const initDefaultCanvas = (tr = { type: 'intent', value: '' }) => {
    const isEvent = tr.type === 'event';
    setNodes([
      {
        id: 'trigger-1',
        type: 'triggerNode',
        position: { x: 100, y: 150 },
        data: {
          triggerType: tr.type || 'intent',
          intentValue: isEvent ? '' : (tr.value || ''),
          eventValue: isEvent ? (tr.value || '') : '',
          inactivityIntervals: tr.type === 'inactivity' ? (tr.value ? String(tr.value).split(',') : ['3h']) : ['3h'],
          expiryH: tr.type === 'chat_expiry_reminder' ? String(Math.floor(Number(tr.value || 120) / 60)) : '2',
          expiryM: tr.type === 'chat_expiry_reminder' ? String(Number(tr.value || 120) % 60) : '0',
          locked: isEvent ? false : !!tr.value,
          availableIntents: tags,
          onChange: updateTriggerIntent,
          onTriggerConfig: onTriggerConfig
        }
      }
    ]);
    setEdges([]);
  };

  // Load backend data
  useEffect(() => {
    if (!workspaceUuid) return;
    
    // Fetch Tags (Intents)
    axios.get(`${API_URL}/api/tags?workspaceUuid=${workspaceUuid}`).then(res => {
      setTags(res.data);
    }).catch(console.error);

    // Fetch Approved WhatsApp Templates
    axios.get(`${API_URL}/api/integrations/whatsapp/templates?workspaceUuid=${workspaceUuid}`).then(res => {
      setTemplates(res.data.data?.filter(t => t.status === 'APPROVED') || []);
    }).catch(console.error);

    // Fetch Workspace Users for Ticket Assignment
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/users/workspace-users`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setWorkspaceUsers(res.data || []);
    }).catch(console.error);

    // Fetch AI Agents
    axios.get(`${API_URL}/api/ai-agents?workspaceUuid=${workspaceUuid}`).then(res => {
      setAiAgents(res.data || []);
    }).catch(console.error);

    // Fetch Specific Workflow if editing
    if (automationId && automationId !== 'new') {
      axios.get(`${API_URL}/api/workflows?workspaceUuid=${workspaceUuid}`).then(res => {
        const wf = res.data.find(w => w._id === automationId);
        if (wf) {
          setWorkflowName(wf.name);
          setMode(wf.mode || (wf.isActive ? 'active' : 'paused'));
          setTestContacts({ whatsapp: '', instagram: '', facebook: '', website: '', ...(wf.testContacts || {}) });
          {
            const pls = (wf.platforms && wf.platforms.length) ? wf.platforms : [wf.platform || 'whatsapp'];
            setPlatforms(pls);
            setPlatform(pls[0] || 'whatsapp');
          }
          const tr = wf.trigger || { type: 'intent', value: '' };
          setTriggerInfo(tr);
          if (tr.type === 'intent') setLockedIntent(tr.value || null);

          if (wf.uiNodes && wf.uiNodes.length > 0) {
            setNodes(wf.uiNodes);
            setEdges(wf.uiEdges || []);
          } else {
            initDefaultCanvas(tr);
          }
        } else {
          initDefaultCanvas();
        }
      }).catch(err => {
        console.error(err);
        initDefaultCanvas();
      });
    } else {
      initDefaultCanvas();
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid, automationId]);

  // Sync data arrays into nodes directly whenever tags/templates change
  useEffect(() => {
    setNodes((nds) => nds.map(n => {
      if (n.type === 'triggerNode') return { ...n, data: { ...n.data, availableIntents: tags, onChange: updateTriggerIntent, onTriggerConfig: onTriggerConfig, triggerType: triggerInfo.type, ...(triggerInfo.type === 'event' ? { eventValue: triggerInfo.value } : triggerInfo.type === 'inactivity' ? { inactivityIntervals: n.data.inactivityIntervals || (triggerInfo.value ? String(triggerInfo.value).split(',') : ['3h']) } : triggerInfo.type === 'chat_expiry_reminder' ? { expiryH: n.data.expiryH ?? String(Math.floor(Number(triggerInfo.value || 120) / 60)), expiryM: n.data.expiryM ?? String(Number(triggerInfo.value || 120) % 60) } : (lockedIntent ? { intentValue: lockedIntent, locked: true } : {})) } };
      if (n.type === 'conditionNode') return { ...n, data: { ...n.data, onContentChange: updateActionContent, onDelete: deleteNode } };
      if (n.type === 'actionNode') return { ...n, data: { ...n.data, availableTemplates: templates, workspaceUsers: workspaceUsers, triggerType: triggerInfo.type, eventValue: triggerInfo.value, onTypeChange: updateActionType, onContentChange: updateActionContent, onDelete: deleteNode } };
      if (n.type === 'aiAgentNode') return { ...n, data: { ...n.data, availableAgents: aiAgents, onContentChange: updateActionContent, onDelete: deleteNode } };
      if (n.type === 'confirmationAgentNode') return { ...n, data: { ...n.data, onContentChange: updateActionContent, onDelete: deleteNode } };
      if (n.type === 'confirmOrderNode' || n.type === 'cancelOrderNode') return { ...n, data: { ...n.data, onDelete: deleteNode } };
      if (n.type === 'shopifyOrdersNode' || n.type === 'postexNode') return { ...n, data: { ...n.data, onContentChange: updateActionContent, onDelete: deleteNode } };
      return n;
    }));
  }, [tags, templates, workspaceUsers, aiAgents, lockedIntent, triggerInfo, updateTriggerIntent, onTriggerConfig, updateActionType, updateActionContent, deleteNode]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: '#111111', strokeWidth: 2 } }, eds)),
    [setEdges]
  );
  const addNewActionNode = (typeStr) => {
    setShowAddBlockPopup(false);
    const newNodeId = `action-${Date.now()}`;
    const lastNode = nodes[nodes.length - 1];
    const newX = lastNode ? lastNode.position.x + 350 : 450;
    const newY = lastNode ? lastNode.position.y : 150;
    const newNode = {
      id: newNodeId,
      type: 'actionNode',
      position: { x: newX, y: newY },
      data: { actionType: typeStr, textBody: '', templateName: '', notificationCategory: '', notificationContext: '', notificationLevel: 'medium', ticketCategory: '', ticketQuery: '', ticketAssignedTo: '', buttonText: 'Yes, please', buttons: ['Yes, please'], question: '', yesLabel: 'Yes', noLabel: 'No', availableTemplates: templates, workspaceUsers: workspaceUsers, onTypeChange: updateActionType, onContentChange: updateActionContent, onDelete: deleteNode }
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const addNewConditionNode = () => {
    setShowAddBlockPopup(false);
    const newNodeId = `condition-${Date.now()}`;
    const lastNode = nodes[nodes.length - 1];
    const newX = lastNode ? lastNode.position.x + 350 : 450;
    const newY = lastNode ? lastNode.position.y : 150;

    const newNode = {
      id: newNodeId,
      type: 'conditionNode',
      position: { x: newX, y: newY },
      data: { statement: '', options: ['Yes', 'No'], onContentChange: updateActionContent, onDelete: deleteNode }
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const addNewAiAgentNode = () => {
    setShowAddBlockPopup(false);
    const newNodeId = `agent-${Date.now()}`;
    const lastNode = nodes[nodes.length - 1];
    const newX = lastNode ? lastNode.position.x + 350 : 450;
    const newY = lastNode ? lastNode.position.y : 150;
    const newNode = {
      id: newNodeId,
      type: 'aiAgentNode',
      position: { x: newX, y: newY },
      data: { aiAgentUuid: '', availableAgents: aiAgents, onContentChange: updateActionContent, onDelete: deleteNode }
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const addNewConfirmationAgentNode = () => {
    setShowAddBlockPopup(false);
    const newNodeId = `confirm-${Date.now()}`;
    const lastNode = nodes[nodes.length - 1];
    const newX = lastNode ? lastNode.position.x + 350 : 450;
    const newY = lastNode ? lastNode.position.y : 150;
    const newNode = {
      id: newNodeId,
      type: 'confirmationAgentNode',
      position: { x: newX, y: newY },
      data: { confirmSubject: '', onContentChange: updateActionContent, onDelete: deleteNode }
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const addShopifyOrdersNode = () => {
    setShowAddBlockPopup(false);
    const newNodeId = `shoporders-${Date.now()}`;
    const lastNode = nodes[nodes.length - 1];
    const newX = lastNode ? lastNode.position.x + 350 : 450;
    const newY = lastNode ? lastNode.position.y : 150;
    setNodes((nds) => [...nds, { id: newNodeId, type: 'shopifyOrdersNode', position: { x: newX, y: newY }, data: { filterByPhone: true, excludeDelivered: true, onContentChange: updateActionContent, onDelete: deleteNode } }]);
  };

  const addPostexNode = () => {
    setShowAddBlockPopup(false);
    const newNodeId = `postex-${Date.now()}`;
    const lastNode = nodes[nodes.length - 1];
    const newX = lastNode ? lastNode.position.x + 350 : 450;
    const newY = lastNode ? lastNode.position.y : 150;
    setNodes((nds) => [...nds, { id: newNodeId, type: 'postexNode', position: { x: newX, y: newY }, data: { onContentChange: updateActionContent, onDelete: deleteNode } }]);
  };

  const addShopifyTagNode = (kind) => {
    setShowAddBlockPopup(false);
    const newNodeId = `${kind}order-${Date.now()}`;
    const lastNode = nodes[nodes.length - 1];
    const newX = lastNode ? lastNode.position.x + 350 : 450;
    const newY = lastNode ? lastNode.position.y + (kind === 'cancel' ? 160 : 0) : 150;
    const newNode = {
      id: newNodeId,
      type: kind === 'confirm' ? 'confirmOrderNode' : 'cancelOrderNode',
      position: { x: newX, y: newY },
      data: { onDelete: deleteNode }
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const handleSaveWorkflow = async () => {
    if (readOnly) { alert('Not authorized for test accounts — demo accounts can preview flows but cannot save changes.'); return; }
    setIsSaving(true);
    try {
      const triggerNode = nodes.find(n => n.type === 'triggerNode');

      let triggerOut;
      if (triggerInfo.type === 'event') {
        triggerOut = { type: 'event', value: triggerInfo.value };
      } else if (triggerInfo.type === 'inactivity') {
        const ivals = (triggerNode?.data?.inactivityIntervals && triggerNode.data.inactivityIntervals.length) ? triggerNode.data.inactivityIntervals : ['3h'];
        triggerOut = { type: 'inactivity', value: ivals.join(',') };
      } else if (triggerInfo.type === 'chat_expiry_reminder') {
        const mins = Number(triggerNode?.data?.expiryH || 0) * 60 + Number(triggerNode?.data?.expiryM || 0);
        triggerOut = { type: 'chat_expiry_reminder', value: String(mins > 0 ? mins : 120) };
      } else {
        if (!triggerNode?.data?.intentValue) {
          alert("Please explicitly select an Intent in the Trigger Node.");
          setIsSaving(false);
          return;
        }
        triggerOut = { type: 'intent', value: triggerNode.data.intentValue };
      }

      const badPreConfirmed = nodes.find(
        n => n.type === 'aiAgentNode' && n.data?.preConfirmed && !(n.data?.confirmedText || '').trim()
      );
      if (badPreConfirmed) {
        alert(`The "Pre-confirmed" AI Agent block needs a confirmed-text value. Type what the user already confirmed (e.g. "i want to process a refund").`);
        setIsSaving(false);
        return;
      }

      const actions = nodes
        .filter(n => n.type === 'actionNode' || n.type === 'conditionNode' || n.type === 'aiAgentNode' || n.type === 'confirmationAgentNode')
        .map(n => {
          if (n.type === 'aiAgentNode') {
            return { id: n.id, type: 'run_ai_agent', config: {
              aiAgentUuid: n.data.aiAgentUuid,
              preConfirmed: !!n.data.preConfirmed,
              confirmedText: n.data.confirmedText || ''
            } };
          }
          if (n.type === 'confirmationAgentNode') {
            return { id: n.id, type: 'run_confirmation_agent', config: { confirmSubject: n.data.confirmSubject } };
          }
          if (n.type === 'conditionNode') {
            return { id: n.id, type: 'ai_condition', config: { statement: n.data.statement, options: n.data.options } };
          }
          return {
            id: n.id, type: n.data.actionType,
            config: {
              body: n.data.textBody, templateName: n.data.templateName,
              notificationCategory: n.data.notificationCategory,
              notificationContext: n.data.notificationContext,
              notificationLevel: n.data.notificationLevel,
              ticketCategory: n.data.ticketCategory,
              ticketQuery: n.data.ticketQuery,
              ticketAssignedTo: n.data.ticketAssignedTo,
              buttonText: n.data.buttonText,
              buttons: n.data.buttons,
              question: n.data.question,
              yesLabel: n.data.yesLabel,
              noLabel: n.data.noLabel,
            }
          };
        });

      // Strip transient/heavy data from each node before saving — these arrays
      // (templates, users, tags, agents) + callbacks are re-injected on load and
      // otherwise bloat the payload (causing 413 Payload Too Large).
      const slimNodes = nodes.map(n => {
        const d = { ...(n.data || {}) };
        delete d.availableTemplates; delete d.workspaceUsers; delete d.availableIntents; delete d.availableAgents;
        delete d.onContentChange; delete d.onDelete; delete d.onTypeChange; delete d.onChange; delete d.onTriggerConfig;
        return { ...n, data: d };
      });

      const payload = {
        workspaceUuid,
        workflowId: automationId !== 'new' ? automationId : null,
        name: workflowName,
        isActive: mode !== 'paused',
        mode,
        testContacts,
        platform,
        platforms,
        trigger: triggerOut,
        actions,
        uiNodes: slimNodes,
        uiEdges: edges
      };

      const res = await axios.post(`${API_URL}/api/workflows/save`, payload);
      
      alert('Workflow saved successfully!');
      
      // If was new, redirect to the exact ID
      if (automationId === 'new') {
        navigate(`/workspace/${workspaceUuid}/automations/${res.data._id}`);
      }
    } catch (err) {
      alert('Error saving flow: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const applyMode = async (newMode, contacts = testContacts) => {
    if (readOnly) { alert('Not authorized for test accounts — demo accounts cannot change automation status.'); return; }
    const prev = mode;
    setMode(newMode);
    if (automationId && automationId !== 'new') {
      try {
        await axios.patch(`${API_URL}/api/workflows/${automationId}/toggle`, { mode: newMode, testContacts: contacts });
      } catch (err) {
        console.error(err);
        setMode(prev); // revert
      }
    }
  };

  return (
    <div style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#F9F4EE' }}>

      {/* HEADER BAR */}
      <div style={{ padding: '12px 22px', background: '#FFFFFF', borderBottom: '1px solid #E7E1D6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => navigate(`/workspace/${workspaceUuid}/automations`)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#FFFFFF', border: '1px solid #E7E1D6', color: '#3A3A37', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '7px 12px', borderRadius: 999 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            Back
          </button>
          <div style={{ width: 1, height: 28, background: '#E7E1D6' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span style={{ width: 38, height: 38, borderRadius: 10, background: '#111111', display: 'grid', placeItems: 'center', color: '#F9F4EE', flex: 'none', boxShadow: 'none' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="2.4" /><circle cx="18" cy="18" r="2.4" /><path d="M8.4 6H14a4 4 0 0 1 4 4v5.6" /></svg>
            </span>
            <div>
              <input value={workflowName} onChange={(e) => setWorkflowName(e.target.value)} placeholder="Untitled automation"
                style={{ fontSize: 17, fontWeight: 700, color: '#111111', border: 'none', outline: 'none', background: 'transparent', padding: 0, minWidth: 130, letterSpacing: '-0.03em' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#9C978C', fontSize: 11.5, fontWeight: 600 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#111111' }} /> Workflow Builder
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'inline-flex', background: '#FAF7F1', borderRadius: 999, padding: 3, gap: 2, border: '1px solid #E7E1D6' }}>
            {[
              { v: 'paused', label: 'Paused', color: '#6B6862' },
              { v: 'testing', label: 'Testing', color: '#8A5A12' },
              { v: 'active', label: 'Active', color: '#0F6E56' },
            ].map(m => {
              const on = mode === m.v;
              return (
                <button key={m.v} onClick={() => applyMode(m.v)} disabled={readOnly}
                  title={readOnly ? 'Not available on demo accounts' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: readOnly ? 'not-allowed' : 'pointer', padding: '6px 13px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, background: on ? '#111111' : 'transparent', color: on ? '#F9F4EE' : '#9C978C', boxShadow: 'none', opacity: readOnly ? 0.55 : 1, transition: 'all .12s' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.color }} />
                  {m.label}
                </button>
              );
            })}
          </div>
          <button onClick={handleSaveWorkflow} disabled={isSaving || readOnly}
            title={readOnly ? 'Not authorized for test accounts' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: readOnly ? '#9C978C' : '#111111', color: '#F9F4EE', border: 'none', padding: '10px 18px', borderRadius: 999, fontWeight: 700, letterSpacing: '-0.03em', fontSize: 13.5, cursor: (isSaving || readOnly) ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1, boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></svg>
            {readOnly ? 'View only' : isSaving ? 'Saving…' : 'Save & Deploy'}
          </button>
        </div>
      </div>

      {/* TESTING BAR — only runs the automation for these contacts */}
      {mode === 'testing' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '10px 22px', background: '#F5EAD5', borderBottom: '1px solid #8A5A12' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#8A5A12', display: 'flex', alignItems: 'center', gap: 7 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>
            Testing — runs only for:
          </span>
          {platforms.map(p => (
            <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: '#8A5A12', textTransform: 'capitalize' }}>{p}</span>
              <input value={testContacts[p] || ''}
                onChange={(e) => setTestContacts(prev => ({ ...prev, [p]: e.target.value }))}
                onBlur={() => applyMode('testing', testContacts)}
                placeholder={p === 'whatsapp' ? 'e.g. 923001234567' : p === 'website' ? 'visitor id' : `${p} user id`}
                style={{ padding: '7px 11px', fontSize: 12.5, borderRadius: 8, border: '1px solid #8A5A12', outline: 'none', background: '#fff', color: '#111111', minWidth: 210 }} />
            </div>
          ))}
          <span style={{ fontSize: 11, color: '#8A5A12', fontWeight: 600 }}>Saved automatically on blur.</span>
        </div>
      )}

      {/* CANVAS AREA */}
      <div style={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          defaultViewport={{ x: 100, y: 50, zoom: 0.9 }}
          minZoom={0.2}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#E7E1D6" gap={24} size={2} />
          <Controls />
        </ReactFlow>

        {/* Blocks panel — rendered outside React Flow so it never gets clipped */}
        <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 5, background: '#FFFFFF', padding: '16px', borderRadius: '14px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)', border: '1px solid #E7E1D6' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 700, color: '#111111', letterSpacing: '-0.03em' }}>Blocks</h3>
          <button
            onClick={() => setShowAddBlockPopup(true)}
            style={{ background: '#FAF7F1', color: '#111111', border: '1px dashed #9C978C', padding: '8px 16px', borderRadius: '999px', fontWeight: 700, cursor: 'pointer', width: '100%' }}>
            + Add Action Block
          </button>
        </div>

        {/* Add Block Popup */}
        {showAddBlockPopup && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(17,17,17,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#FFFFFF', borderRadius: '20px', width: '560px', maxWidth: '92vw', maxHeight: '84vh', boxShadow: '4px 4px 0 rgba(17,17,17,.2)', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #E7E1D6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid #E7E1D6' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111111', letterSpacing: '-0.03em' }}>Add a block</h3>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9C978C', fontWeight: 600 }}>Pick a step to add to your automation.</p>
                </div>
                <button onClick={() => setShowAddBlockPopup(false)} style={{ width: 32, height: 32, display: 'grid', placeItems: 'center', background: '#FAF7F1', border: 'none', borderRadius: 9, cursor: 'pointer', color: '#6B6862' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>
              <div style={{ padding: '18px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <PaletteSection label="Messaging">
                  {platforms.length > 1 ? (
                    <BlockTile onClick={() => addNewActionNode('send_whatsapp_message')} color="#111111" icon={ICONS.message} title="Custom Message" sub="All platforms" />
                  ) : (
                    <>
                      {platform === 'whatsapp' && <BlockTile onClick={() => addNewActionNode('send_whatsapp_message')} color="#111111" icon={ICONS.message} title="Custom Message" />}
                      {platform === 'whatsapp' && <BlockTile onClick={() => addNewActionNode('send_whatsapp_template')} color="#111111" icon={ICONS.template} title="WhatsApp Template" />}
                      {platform === 'website' && <BlockTile onClick={() => addNewActionNode('send_website_message')} color="#111111" icon={ICONS.message} title="Website Message" />}
                      {platform === 'instagram' && <BlockTile onClick={() => addNewActionNode('send_instagram_message')} color="#111111" icon={ICONS.message} title="Custom Message" />}
                      {platform === 'facebook' && <BlockTile onClick={() => addNewActionNode('send_facebook_message')} color="#111111" icon={ICONS.message} title="Custom Message" />}
                    </>
                  )}
                  <BlockTile onClick={() => addNewActionNode('send_message_with_button')} color="#111111" icon={ICONS.button} title="Message + Button" sub="Reply button" />
                </PaletteSection>

                <PaletteSection label="Query Resolution">
                  <BlockTile onClick={() => addNewActionNode('ask_query_resolved')} color="#111111" icon={ICONS.ask} title="Ask: Query Resolved?" sub="Yes / No branches" />
                  <BlockTile onClick={() => addNewActionNode('kill_inactivity_trigger')} color="#0F6E56" icon={ICONS.check} title="Kill Inactivity" sub="Resolve · stop loop" />
                  <BlockTile onClick={() => addNewActionNode('restart_inactivity_trigger')} color="#8A5A12" icon={ICONS.restart} title="Restart Inactivity" sub="Restart the intervals" />
                </PaletteSection>

                <PaletteSection label="Flow & AI">
                  <BlockTile onClick={addNewConditionNode} color="#111111" icon={ICONS.brain} title="AI Condition" sub="Branch on intent" />
                  <BlockTile onClick={addNewAiAgentNode} color="#111111" icon={ICONS.robot} title="AI Agent" />
                  <BlockTile onClick={addNewConfirmationAgentNode} color="#111111" icon={ICONS.check} title="Confirmation Agent" />
                </PaletteSection>

                <PaletteSection label="Support">
                  <BlockTile onClick={() => addNewActionNode('create_notification')} color="#111111" icon={ICONS.bell} title="System Notification" />
                  <BlockTile onClick={() => addNewActionNode('create_ticket')} color="#8A5A12" icon={ICONS.ticket} title="Support Ticket" />
                </PaletteSection>

                {platforms.length === 1 && platform === 'whatsapp' && (
                  <PaletteSection label="Shopify">
                    <BlockTile onClick={addShopifyOrdersNode} color="#0F6E56" icon={ICONS.cart} title="Shopify Orders" sub="Filter + branch" />
                    <BlockTile onClick={addPostexNode} color="#111111" icon={ICONS.truck} title="PostEx Tracking" />
                    <BlockTile onClick={() => addShopifyTagNode('confirm')} color="#0F6E56" icon={ICONS.check} title="Confirm Order" sub="Shopify tag" />
                    <BlockTile onClick={() => addShopifyTagNode('cancel')} color="#B23A3A" icon={ICONS.stop} title="Cancel Order" sub="Shopify tag" />
                  </PaletteSection>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

    </div>
  );
};

export default AutomationEditor;
